import * as React from "react";
import { toast } from "react-toastify";
import { CellContext } from "@tanstack/react-table";

import { FilterParams } from "src/library/utils";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { Table } from "src/core/dashboard/components/widgets/index";
import { LikeToDo } from "src/library/components/LikeToDo";
import { Modal } from "src/library/components/Modal";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PaginatedParams } from "src/core/shared/api";
import { PageProps } from "src/pages/Router/routes";
import {
  useLeadTimeOverrideApi,
  ILeadTimeOverride
} from "src/modules/lead-time-override/api/lead-time-override.api";
import { leadTimeOverridesTableColumns } from "src/core/shared/components/tables/leadTimeOverridesTableColumns";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { GLOBAL_SETTINGS_CACHE_KEY } from "src/core/shared/api/globalSettings.api";
import useCache from "src/core/shared/hooks/useCache";
import { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";

const defaultPagination: PaginatedParams<ILeadTimeOverride> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "deliveryDate", "DESC" ] ]
};

/**
 * View LeadTimeOverrides
 *
 */
const LeadTimeOverridesPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { history } = props;
  const TableRef = React.useRef<TableRefProps>(null);
  const [ leadTimeOverridesResponse, getLeadTimeOverrides ] = useLeadTimeOverrideApi("LIST_LEAD_TIME_OVERRIDES");
  const leadTimeOverridesData = React.useMemo(() => leadTimeOverridesResponse.data?.result || [], [ leadTimeOverridesResponse.data?.result ]);
  const [ deleteLeadTimeOverrideResponse, deleteLeadTimeOverride ] = useLeadTimeOverrideApi("DELETE_LEAD_TIME_OVERRIDE");
  const [ modalData, setModalData ] = React.useState<number | null>(null);
  const checkPermission = useCheckUserPermission();
  const canCreate = checkPermission("create-global-settings");
  const canAmend = checkPermission("amend-global-settings");
  const canDelete = checkPermission("delete-global-settings");
  const { removeItemByKey } = useCache();

  const onFetchData = React.useCallback((params: {filters: FilterParams<ILeadTimeOverride>, pagination: PaginatedParams<ILeadTimeOverride>}) => {
    getLeadTimeOverrides({
      pagination: params.pagination,
      filters: params.filters
    });
  }, [ getLeadTimeOverrides ]);

  const editLeadTimeOverrideAction = React.useCallback((id: number) => {
    history.push(`/platform/lead-time-overrides/edit/${id}`);
  }, [ history ]);

  const deleteLeadTimeOverrideAction = (id: number) => {
    setModalData(id);
  };

  const deleteLeadTimeOverrideActionConfirm = () => {
    if (modalData) {
      deleteLeadTimeOverride(modalData);
      setModalData(null);
    }
  };

  // On leadTimeOverride deletion
  React.useEffect(() => {
    if (deleteLeadTimeOverrideResponse.data) {
      toast.success("The leadTimeOverride has been successfully deleted.");
      removeItemByKey(GLOBAL_SETTINGS_CACHE_KEY);

      if (TableRef.current) {
        TableRef.current.reloadTable();
      }
    }

    if (deleteLeadTimeOverrideResponse.error) {
      toast.error(`Could not delete leadTimeOverride - ${deleteLeadTimeOverrideResponse.error.message}`);
    }
  }, [ deleteLeadTimeOverrideResponse, removeItemByKey ]);

  /**
   * Table actions
   */
  const tableActions = React.useCallback((info: CellContext<ILeadTimeOverride, unknown>) => {
    const actions = [];

    if (canAmend) {
      actions.push({
        title: "Edit",
        action: () => editLeadTimeOverrideAction(info.row.original.id)
      });
    }

    if (canDelete) {
      actions.push({
        title: "Delete",
        action: () => deleteLeadTimeOverrideAction(info.row.original.id)
      });
    }

    return (
      <ActionButton
        title="Actions"
        items={actions}
      />
    );
  }, [
    canAmend,
    canDelete,
    editLeadTimeOverrideAction
  ]);

  const tableColumns = React.useMemo(() => leadTimeOverridesTableColumns(tableActions), [ tableActions ]);

  return (
    <Layout
      {...props}
      title="Lead Time Overrides"
    >
      <MainContentWrapper
        fill
        Header={canCreate && (
          <LikeToDo
            copy="What would you like to do?"
            buttons={[
              <Button
                key="new-leadTimeOverride"
                onClick={() => history.push("/platform/lead-time-overrides/new")}
              >
                New Lead Time Override
              </Button>
            ]}
          />
        )}
      >
        <Table
          tableOf="Lead Time Overrides"
          className="large"
          data={leadTimeOverridesData}
          onFetchData={onFetchData}
          defaultVisibility={{
            deliveryDate: true,
            type: true,
            cutoffDateTime: true,
            actions: true
          }}
          defaultPagination={defaultPagination}
          loading={leadTimeOverridesResponse.pending}
          pageCount={leadTimeOverridesResponse.data?.pages || 0}
          rowCount={leadTimeOverridesResponse.data?.count || 0}
          error={leadTimeOverridesResponse.error}
          columns={tableColumns}
          ref={TableRef}
          mainAction={row => history.push(`/orders/${row.original.id}`)}
          pageSizeOptions={[
            10,
            20,
            50,
            100
          ]}
        />

        {/* <Table
          tableId="global"
          ref={TableRef}
          error={leadTimeOverridesResponse.error}
          loading={leadTimeOverridesResponse.pending}
          tableOf="Lead Time Overrides"
          data={leadTimeOverridesDate ? leadTimeOverridesDate.result : []}
          onFetchData={onFetchData}
          totalRows={leadTimeOverridesDate ? leadTimeOverridesDate.count : 0}
          pages={leadTimeOverridesDate ? leadTimeOverridesDate.pages : 0}
          defaultPagination={defaultPagination}
          mainAction={row => editLeadTimeOverrideAction(row.original.id)}
          columns={leadTimeOverridesTableColumns(row => {
            const actions = tableActions(row);

            return actions.length ? (
              <ActionButton
                title="Actions"
                items={tableActions(row)}
              />
            ) : null;
          })}
        /> */}
      </MainContentWrapper>

      <Modal
        small
        show={modalData !== null}
        type="confirm"
        title="Are you sure?"
        body="Deleting this override cannot be undone."
        backgroundAction={() => setModalData(null)}
        actions={[
          {
            name: "No, cancel",
            action: () => setModalData(null),
            $buttonType: "secondary"
          },
          {
            name: "I confirm",
            action: deleteLeadTimeOverrideActionConfirm,
            $buttonType: "primary"
          }
        ]}
      />
    </Layout>
  );
};

export default LeadTimeOverridesPage;
